import { CALCULATION_TYPES, ORION_ENTITY_GROUPINGS } from './constants'

/**
 * Merges global date settings as fallback into column options.
 * If the column has dateMode 'default' (or no dateMode), inherit the global date config.
 * Otherwise the column's own date settings take precedence.
 */
function resolveColumnOptions (colOptions, globalDateConfig) {
  const mode = colOptions?.dateMode
  if (mode && mode !== 'default') return colOptions

  return {
    ...colOptions,
    dateMode: globalDateConfig.dateMode,
    quickDate: globalDateConfig.quickDate,
    startDate: globalDateConfig.startDate,
    endDate: globalDateConfig.endDate
  }
}

function buildCalcs (columns, globalDateConfig) {
  return columns.map(col => {
    const typeDef = CALCULATION_TYPES[col.type]
    if (!typeDef) return null
    const options = resolveColumnOptions(col.options, globalDateConfig)
    return typeDef.buildCalculation(col.id, options)
  }).filter(Boolean)
}

function nestGroupings (groupings, calcs) {
  return [
    groupings.reduceRight((innerCalcs, grouping, index) => [{
      id: `grouping_${index}_${grouping}`,
      $type: 'grouping',
      grouping,
      calculations: [...calcs, ...innerCalcs]
    }], [])[0]
  ]
}

/**
 * Builds the Orion Reporting/Scope request body from user config.
 * Includes entity/entityIds for Orion scoping. The frontend owns entity resolution.
 */
export function buildOrionRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const effectiveGroupings = maxGroupingDepth != null
    ? groupings.slice(0, maxGroupingDepth)
    : groupings

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = nestGroupings(effectiveGroupings, calcs)
  }

  return request
}

/**
 * Builds a child request for lazy expand — uses remaining groupings
 * starting from the given depth index, scoped to a specific group entity.
 * Pass maxGroupingDepth to limit how many grouping levels are included.
 */
export function buildChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, depthIndex, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = nestGroupings(effectiveGroupings, calcs)
  }

  return request
}

/**
 * Builds a child request for virtual (non-entity-scopable) groupings.
 * Scopes to the nearest entity-scopable ancestor and adds a string-equal
 * filter on the virtual grouping name to narrow results server-side.
 * Pass maxGroupingDepth to limit how many inner grouping levels are included.
 */
export function buildFilteredChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, {
  asOfDate,
  depthIndex,
  maxGroupingDepth,
  entity,
  entityIds,
  filterGrouping,
  filterName
}) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  if (effectiveGroupings.length === 0) {
    return { entity, entityIds, asOfDate, calculations: calcs }
  }

  const innerGrouping = nestGroupings(effectiveGroupings, calcs)[0]

  const wrappedCalc = {
    id: `filter_${filterGrouping}`,
    $type: 'grouping',
    grouping: filterGrouping,
    filters: [{
      $type: 'string-equal',
      path: ORION_ENTITY_GROUPINGS.has(filterGrouping)
        ? `group-info:${filterGrouping}:Name`
        : 'group-info',
      haystack: [filterName]
    }],
    calculations: [innerGrouping]
  }

  return { entity, entityIds, asOfDate, calculations: [wrappedCalc] }
}
